// Keeps a phone's screen from dimming/locking while `active` — used for a live voice session, where
// a locked screen suspends the mic and the page (iOS always, Android usually) and so drops the call.
//
// Uses the Screen Wake Lock API (Chrome/Edge/Samsung on Android, Safari 16.4+). The browser releases
// the lock whenever the tab is hidden (app switch, notification shade), so it is re-acquired when the
// tab is visible again. Where the API is missing (older iOS) it falls back to NoSleep.js — a muted,
// looping, invisible video — loaded only on those devices. That fallback must start from a user
// gesture, which the voice-start tap is.
//
// Never throws: unsupported, denied (e.g. battery saver) or an insecure origin (plain http on a LAN
// IP) just means the screen may lock — voice itself is unaffected.
import { useEffect } from 'react';

type Sentinel = { released: boolean; release: () => Promise<void> };
type NoSleepLike = { enable: () => Promise<unknown>; disable: () => void };

export function useScreenWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;

    let cancelled = false;
    let sentinel: Sentinel | null = null;
    let noSleep: NoSleepLike | null = null;
    const wakeLock = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<Sentinel> } }).wakeLock;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (wakeLock) {
        if (sentinel && !sentinel.released) return;
        try {
          const s = await wakeLock.request('screen');
          if (cancelled) { void s.release(); return; }
          sentinel = s;
        } catch {
          // denied / not allowed right now — nothing to do; the next visibility change retries
        }
        return;
      }
      if (noSleep) return;
      try {
        const { default: NoSleep } = await import('nosleep.js');
        if (cancelled) return;
        const ns = new NoSleep();
        await ns.enable();
        if (cancelled) { ns.disable(); return; }
        noSleep = ns;
      } catch {
        // autoplay blocked or unsupported — the screen may lock; voice is unaffected
      }
    };

    const onVisibility = () => { if (document.visibilityState === 'visible') void acquire(); };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (sentinel && !sentinel.released) void sentinel.release().catch(() => undefined);
      noSleep?.disable();
    };
  }, [active]);
}
